import {
  Autocomplete,
  Button,
  Checkbox,
  IconButton,
  type Item,
  SelectInput,
  SelectInputItem,
  Tag,
} from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, DatePicker } from '@/shared/ui/atoms';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';
import { useBulkLink } from '../../../hooks/useBulkLink';
import { fmtBR } from '../../../utils/format';

/** Valor sentinela para a opção "sem módulo" (Radix Select não aceita value vazio). */
const NO_MODULE = '__none__';

/**
 * Modal de vínculo em lote (Radix Dialog). O `Dialog.Root` é renderizado de forma
 * persistente e controlado por `open`/`onOpenChange` (ver D9). É `modal={false}` para que
 * o popover do `DatePicker` (portado para o body) permaneça clicável — no modo modal o
 * Radix bloqueia os `pointer-events` fora do conteúdo do diálogo, impedindo a seleção de
 * datas no calendário. O overlay em tela cheia continua cobrindo o fundo; o fechamento por
 * interação externa é desabilitado (ver `onInteractOutside`) para que fechar dropdowns
 * internos não feche o modal — ele fecha apenas pelo X, "Cancelar" ou Esc.
 */
export function BulkLinkModal() {
  const { t } = useTranslation('gradeAttendanceConfig');
  const { t: tCommon } = useTranslation('common');
  const {
    open,
    from,
    to,
    search,
    module,
    picked,
    rows,
    searchResults,
    isQuerySuccess,
    hasSearched,
    isListingFetching,
    isDateRangeInvalid,
    allChecked,
    selectedCount,
    moduleOptions,
    close,
    setFrom,
    setTo,
    setSearch,
    setPicked,
    setModule,
    clear,
    runSearch,
    toggle,
    toggleAll,
    confirm,
  } = useBulkLink();

  const selectableCount = rows.filter(r => !r.linked).length;

  // Incrementado ao limpar para remontar os campos de filtro (DatePicker/Autocomplete
  // mantêm estado interno que nem sempre re-sincroniza ao zerar o valor externo).
  const [clearNonce, setClearNonce] = useState(0);
  function handleClear() {
    clear();
    setClearNonce(n => n + 1);
  }

  // Sugestões do autocomplete de busca — só a partir de 3 caracteres, como no Autocomplete
  // de turma. `rows` já vem filtrado pelo `search`.
  const disciplineItems: Item<string>[] =
    search.length >= 3 ? searchResults.map(r => ({ label: r.name, value: r.id })) : [];
  const disciplineMessage = getAutocompleteSearchMessage(
    search.length,
    isQuerySuccess,
    disciplineItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && close()} modal={false}>
      <Dialog.Portal>
        {/* Backdrop manual: com `modal={false}` o Radix não renderiza o Dialog.Overlay. */}
        <div
          aria-hidden="true"
          className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft"
        />
        <Dialog.Content
          aria-describedby={undefined}
          // Não fechar por interação externa: como o Dialog é `modal={false}`, fechar
          // dropdowns internos (SelectInput / DatePicker, portados para o body) dispara
          // o dismiss do diálogo. O modal fecha apenas pelo X, "Cancelar" ou Esc.
          onInteractOutside={e => e.preventDefault()}
          className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 flex max-h-[85vh] w-full max-w-[47.5rem] flex-col rounded-lg bg-ctx-layout-surface shadow-level4 focus:outline-none"
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b-hairline border-ctx-layout-border px-xxs py-xxxs">
            <div className="flex flex-col gap-quark">
              <Dialog.Title className="text-ctx-content-title ts-heading-xs">
                {t('bulk.title')}
              </Dialog.Title>
              <p className="text-ctx-content-base ts-caption-xxs">{t('bulk.scope')}</p>
            </div>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                ariaLabel={t('bulk.cancel')}
                hierarchy="tertiary"
                size="sm"
              />
            </Dialog.Close>
          </div>

          {moduleOptions.length === 0 ? (
            /* Sem módulos: não há destino para o vínculo — estado informativo no lugar do formulário. */
            <div className="flex flex-1 flex-col items-center justify-center gap-xxs px-xxs py-xl text-center">
              <Alert
                type="warning"
                title={t('bulk.noModulesTitle')}
                description={t('bulk.noModulesDesc')}
              />
              <Dialog.Close asChild>
                <Button hierarchy="secondary" size="md">
                  {t('bulk.cancel')}
                </Button>
              </Dialog.Close>
            </div>
          ) : (
            <>
              {/* Filtros */}
              <div className="flex flex-wrap items-end gap-micro border-b-hairline border-ctx-layout-border px-xxs py-mini">
                <DatePicker
                  key={`from-${clearNonce}`}
                  label={t('bulk.periodFrom')}
                  value={from}
                  onChange={setFrom}
                  size="sm"
                  className="w-[11.5rem]"
                />
                <DatePicker
                  key={`to-${clearNonce}`}
                  label={t('bulk.periodTo')}
                  value={to}
                  onChange={setTo}
                  size="sm"
                  className="w-[11.5rem]"
                  hasError={isDateRangeInvalid}
                  {...(isDateRangeInvalid ? { helper: t('bulk.errorDateRange') } : {})}
                />
                <div className="min-w-[12rem] flex-1">
                  <Autocomplete<string>
                    key={`search-${clearNonce}`}
                    label={t('bulk.disciplineLabel')}
                    placeholder={t('bulk.searchPlaceholder')}
                    size="sm"
                    list={disciplineItems}
                    custom={{ input: '!w-full', optionList: 'z-[60]' }}
                    selectedItem={{ label: search, value: search }}
                    {...(disciplineMessage !== undefined
                      ? { notFoundMessage: disciplineMessage }
                      : {})}
                    onChange={e => setSearch(e.target.value)}
                    handleChange={item => setPicked(item.value ?? '', item.label ?? '')}
                    handleClearQuery={() => setSearch('')}
                  />
                </div>
                <Button
                  hierarchy="primary"
                  size="sm"
                  onClick={runSearch}
                  disabled={!picked.id || isListingFetching || isDateRangeInvalid}
                  isLoading={isListingFetching}
                >
                  {t('bulk.search')}
                </Button>
                <Button hierarchy="secondary" size="sm" onClick={handleClear}>
                  {t('bulk.clear')}
                </Button>
              </div>

              {/* Selecionar todas */}
              <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-xxs py-nano">
                <Checkbox
                  label={t('bulk.selectAll')}
                  checked={allChecked}
                  size="sm"
                  onCheckedChange={() => toggleAll()}
                />
                <span className="tabular-nums text-ctx-content-base ts-caption-xxs">
                  {t('bulk.count', {
                    selected: selectedCount,
                    total: selectableCount,
                  })}
                </span>
              </div>

              {/* Lista */}
              <div className="min-h-[7.5rem] flex-1 overflow-y-auto at-scrollbar">
                {isListingFetching ? (
                  <div className="px-xxs py-sm text-center text-ctx-content-placeholder ts-caption-xxs">
                    {tCommon('search.searching')}
                  </div>
                ) : rows.length === 0 ? (
                  <div className="px-xxs py-sm text-center text-ctx-content-placeholder ts-caption-xxs">
                    {hasSearched ? t('bulk.empty') : t('bulk.initial')}
                  </div>
                ) : (
                  rows.map(r => (
                    <div
                      key={r.id}
                      className={[
                        'flex items-center gap-micro border-b-hairline border-ctx-layout-border px-xxs py-nano',
                        r.linked ? 'opacity-intense' : '',
                      ].join(' ')}
                    >
                      <Checkbox
                        checked={r.checked}
                        disabled={r.linked}
                        size="sm"
                        onCheckedChange={() => toggle(r)}
                        label={`${r.disciplineName} · ${t('bulk.lessonLabel', {
                          number: r.lessonNumber,
                        })}`}
                      />
                      <div className="flex flex-1 items-center justify-end gap-nano">
                        {r.date && (
                          <span className="tabular-nums text-ctx-content-placeholder ts-caption-xxs">
                            {fmtBR(r.date)}
                          </span>
                        )}
                        {r.linked && (
                          <Tag
                            label={t('bulk.linked')}
                            status="positive"
                            size="sm"
                            custom={{ text: 'text-nowrap' }}
                          />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="flex flex-col gap-nano border-t-hairline border-ctx-layout-border px-xxs py-mini">
                <span className="text-ctx-content-placeholder ts-caption-xxs">
                  {t('bulk.note')}
                </span>
                <div className="flex flex-wrap items-end justify-between gap-mini">
                  <div className="min-w-[12rem] flex-1 sm:max-w-[20rem]">
                    <SelectInput
                      label={t('bulk.module')}
                      value={module || NO_MODULE}
                      onValueChange={value => setModule(value === NO_MODULE ? '' : value)}
                      size="sm"
                    >
                      {moduleOptions.map(o => (
                        <SelectInputItem
                          key={o.value || NO_MODULE}
                          value={o.value || NO_MODULE}
                          custom={{ root: 'text-nowrap' }}
                        >
                          {o.value === '' ? t('bulk.noModule') : o.label}
                        </SelectInputItem>
                      ))}
                    </SelectInput>
                  </div>
                  <div className="flex items-end gap-nano">
                    <Dialog.Close asChild>
                      <Button hierarchy="secondary" size="md">
                        {t('bulk.cancel')}
                      </Button>
                    </Dialog.Close>
                    <Button
                      hierarchy="primary"
                      size="md"
                      onClick={confirm}
                      disabled={selectedCount === 0 || !module}
                    >
                      {t('bulk.confirm', { count: selectedCount })}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
